import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Chevron,
  Div,
  DragSortIcon,
  EditPenIcon,
  H5,
  Icon,
  ImagePlaceholder,
  ImagePlaceholderCircle,
  Img,
  P,
  TrashIconRed,
  centsToDollars,
  formatter,
  lightTheme,
} from '@vgrubs/components';

import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Collapsible, ItemListImage } from '../../MenyStyled';
import {
  BTCategoryResponse,
  BTMenuItemResponse,
  BTMenuResponse,
} from 'components/src/types/brand.types';

export const MenuItemTypeComponent = ({
  itemType,
  onClick,
  onClickDelete,
  menu,
  setMenu,
}: {
  itemType: BTCategoryResponse;
  onClick: (item: {
    item: BTMenuItemResponse;
    category: BTCategoryResponse;
  }) => void;
  onClickDelete: (data: {
    item: BTMenuItemResponse;
    category: BTCategoryResponse;
  }) => void;
  menu: BTMenuResponse | null;
  setMenu: Dispatch<SetStateAction<BTMenuResponse | null>>;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (ref?.current) setHeight(ref?.current?.clientHeight);
  }, [menu]);

  const [expanded, setExpanded] = useState(false);

  const [activeId, setActiveId] = useState(null);

  function dragEndEvent(e: any) {
    const { over, active } = e;
    moveItem({ over, active });
  }

  const moveItem = ({
    over,
    active,
  }: {
    over: { id: number };
    active: { id: number };
  }) => {
    const items = itemType?.items;

    const sortedItems = arrayMove(
      items,
      items.findIndex((item) => item.id === active.id),
      items.findIndex((item) => item.id === over?.id)
    );

    setMenu((prev) => {
      if (!prev) return prev;
      const updated = prev.categories.map((category) => {
        if (category.id === itemType.id) {
          return {
            ...category,
            items: sortedItems,
          };
        }
        return category;
      });

      return { ...prev, categories: updated }; // Return updated categories
    });
  };

  function handleDragStart(event: any) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event: any) {
    if (event.over) {
      setActiveId(null);
      dragEndEvent(event);
    }
  }

  return (
    <>
      <Div
        $box
        $p="16px 24px"
        $flex
        $alignCenter
        $between
        $mb="8"
        $background={expanded && lightTheme.primary}
        $pointer
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Div $flex $alignCenter $gap="8px">
          <P $bold $m="0" $color={expanded && lightTheme.white}>
            {itemType.name} ({itemType.items.length})
          </P>
        </Div>
        <Icon
          $pointer
          $rotate={expanded ? '360deg' : '180deg'}
          color={expanded ? lightTheme.white : lightTheme.darkGray}
        >
          <Chevron />
        </Icon>
      </Div>

      <Collapsible expanded={expanded} elementHeight={height}>
        <div ref={ref}>
          <DndContext
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            // modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={itemType?.items?.map((task) => task.id)}
              strategy={verticalListSortingStrategy}
              // disabled={sortingDisabled}
            >
              {itemType?.items?.map((item) => (
                <MenuItemComponent
                  key={item.id}
                  item={item}
                  category={itemType}
                  onClick={onClick}
                  onClickDelete={onClickDelete}
                />
              ))}
            </SortableContext>
            <DragOverlay>
              {activeId &&
                !!itemType?.items?.find((t) => activeId === t.id) && (
                  <MenuItemComponent
                    item={
                      itemType?.items?.find(
                        (t) => activeId === t.id
                      ) as BTMenuItemResponse
                    }
                    category={itemType}
                  />
                )}
            </DragOverlay>
          </DndContext>

          {itemType.items.length === 0 ? (
            <P $m="0" $center>
              No items
            </P>
          ) : null}
        </div>
      </Collapsible>
    </>
  );
};

export const MenuItemComponent = ({
  item,
  onClick,
  onClickDelete,
  key,
  category,
}: {
  item: BTMenuItemResponse;
  onClick?: (data: {
    item: BTMenuItemResponse;
    category: BTCategoryResponse;
  }) => void;
  onClickDelete?: (data: {
    item: BTMenuItemResponse;
    category: BTCategoryResponse;
  }) => void;
  key?: number;
  category: BTCategoryResponse;
}) => {
  const {
    setNodeRef,
    listeners,
    attributes,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    // disabled: disabled,
  });

  const styles = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 0,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <Div
      ref={setNodeRef}
      style={styles}
      {...attributes}
      key={key}
      $box
      $p="12px 24px "
      $flex
      $alignCenter
      $between
      $mb="8"
    >
      <Div
        $width="100%"
        $flex
        $alignCenter
        $gap="16px"
        $pointer
        onClick={() => onClick?.({ item, category })}
      >
        <Div $flex $alignCenter $gap="16px">
          <Icon
            {...listeners}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <DragSortIcon />
          </Icon>
          {item.imageUrl ? (
            <ItemListImage>
              <Img
                alt=""
                height="50px"
                src={item.imageUrl ? item.imageUrl : (ImagePlaceholder as any)}
              />
            </ItemListImage>
          ) : (
            <ImagePlaceholderCircle />
          )}
        </Div>

        <Div $flex $column $gap="2px">
          <H5 $mb="0">{item.name}</H5>
          <P $m="0">
            Suggested Price:{' '}
            <span style={{ color: lightTheme.green }}>
              {formatter.format(centsToDollars(item.fromPrice))} -{' '}
              {formatter.format(centsToDollars(item.toPrice))}
            </span>
          </P>
        </Div>
      </Div>
      <Div $flex $alignCenter $between $gap="16px" $gapSM="8px">
        <Icon $shadow $pointer onClick={() => onClick?.({ item, category })}>
          <EditPenIcon />
        </Icon>
        <Icon $pointer onClick={() => onClickDelete?.({ item, category })}>
          <TrashIconRed />
        </Icon>
      </Div>
    </Div>
  );
};
